import { useEffect, useRef, useState } from 'react';
import type { TierBadge } from '../../../../modules/api';
import { TierCard } from './TierCard';
import { TierSelectorArrow } from './TierSelectorArrow';

export interface TierSelectorProps {
  badges: TierBadge[];
  onSelect?: (poolOfferId: number) => void;
  /** Forwarded to each TierCard — see `TierCardProps['layout']`. */
  layout?: 'auto' | 'desktop';
}

type FadeState = 'none' | 'left' | 'right' | 'both';

function fadeClassFor(state: FadeState): string {
  if (state === 'both') return 'fade-edge-both';
  if (state === 'left') return 'fade-edge-l';
  if (state === 'right') return 'fade-edge-r';
  return '';
}

/** Distance (px) scrolled per arrow click. */
const SCROLL_STEP_PX = 240;

/**
 * Horizontal row of TierCards with hidden scrollbar. The edge fade and
 * the left/right arrow buttons only appear on whichever side still has
 * tiers scrolled out of view — the row's only affordance that more
 * tiers exist off-screen, since the fade alone is easy to miss.
 */
export function TierSelector({ badges, onSelect, layout = 'auto' }: TierSelectorProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState<FadeState>('none');

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const canScrollLeft = el.scrollLeft > 2;
      const canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
      setFade(
        canScrollLeft && canScrollRight
          ? 'both'
          : canScrollLeft
            ? 'left'
            : canScrollRight
              ? 'right'
              : 'none',
      );
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [badges.length]);

  // Keep the active tier visible: whenever the selection moves (slider
  // swipe, tier click, "Buy Another" jump) centre the active card in the
  // row. Runs only on selection change, so it never fights the user's own
  // scrolling. jsdom lacks Element.scrollTo — hence the typeof guard.
  const activeIndex = badges.findIndex((b) => b.isSelf);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || activeIndex < 0) return;
    const card = el.children[activeIndex] as HTMLElement | undefined;
    if (!card || typeof el.scrollTo !== 'function') return;
    const delta = card.getBoundingClientRect().left - el.getBoundingClientRect().left;
    const left = el.scrollLeft + delta - (el.clientWidth - card.clientWidth) / 2;
    el.scrollTo({ left, behavior: 'smooth' });
  }, [activeIndex]);

  if (badges.length === 0) return null;

  const scrollBy = (deltaPx: number) => {
    scrollRef.current?.scrollBy({ left: deltaPx, behavior: 'smooth' });
  };

  return (
    <div data-co-component="TierSelector" className="relative">
      <div
        ref={scrollRef}
        className={`scrollbar-hide ${fadeClassFor(fade)} flex items-center gap-2 overflow-x-auto`.trim()}
      >
        {badges.map((b) => (
          <TierCard
            key={b.poolOfferId}
            badge={b}
            layout={layout}
            onClick={() => onSelect?.(b.poolOfferId)}
          />
        ))}
      </div>
      {(fade === 'left' || fade === 'both') && (
        <TierSelectorArrow direction="left" onClick={() => scrollBy(-SCROLL_STEP_PX)} />
      )}
      {(fade === 'right' || fade === 'both') && (
        <TierSelectorArrow direction="right" onClick={() => scrollBy(SCROLL_STEP_PX)} />
      )}
    </div>
  );
}
